import { useMemo } from 'react';
import { useTeamStore, DEFAULT_GROUP_ID } from '@/stores/team-store';
import { useAuthStore } from '@/stores/auth-store';
import { resolvePermission } from '@/lib/permission-resolve';
import { PERMISSION_LEVELS } from '@/types/settings';
import type {
  SenderPermissionLevel,
  LabelPermissionLevel,
  AssigneePermissionLevel,
  Team,
} from '@/types/settings';

/**
 * Standalone (non-hook) function to get inbox permissions from store state.
 * Safe to call from Zustand stores, callbacks, and other non-React contexts.
 */
export function getInboxPermissions(): { canViewOtherUsersChats: boolean; canViewUnassignedChats: boolean } {
  const { selectedAdminUserId } = useAuthStore.getState();
  const isAdmin = !selectedAdminUserId;
  if (isAdmin) return { canViewOtherUsersChats: true, canViewUnassignedChats: true };

  // Deliberately NOT fail-closed on the load window, unlike the sender and label
  // resolvers. conversation-store reads this once while building the Firestore
  // query and nothing re-runs when teamsLoaded flips, so returning `false` early
  // would pin a shared user to their own chats for the rest of the session. This
  // is a visibility path, not a send path — the permissive default matches what
  // the resolution below returns for the same data anyway. Still kick a load so
  // a boot path that never subscribed catches up. (KAN-14)
  if (!useTeamStore.getState().teamsLoaded) {
    void useTeamStore.getState().loadTeams();
  }

  // Shared membership lookup — same normalised-email matching as every other
  // resolver, so inbox visibility can never resolve against a different team
  // than sender/label permissions (function declarations hoist; team member
  // emails are stored lower-cased, the lookup normalises to match).
  const { userTeam, defaultTeam } = resolveTeamsStandalone();

  const source = userTeam?.permissions?.inbox?.global
    ?? defaultTeam?.permissions?.inbox?.global;

  return {
    canViewOtherUsersChats: source?.users_can_view_other_users_chats ?? true,
    canViewUnassignedChats: source?.users_can_view_unassigned_chats ?? true,
  };
}

/** Resolve {userTeam, defaultTeam} for the signed-in user outside React.
 *  Shared by the standalone resolvers below so they can never drift from each
 *  other (or from the hook) in how membership is matched. */
function resolveTeamsStandalone(): { userTeam: Team | null; defaultTeam: Team | null } {
  const { user } = useAuthStore.getState();
  const teams = useTeamStore.getState().teams;
  const userEmail = user?.email?.toLowerCase();

  let userTeam: Team | null = null;
  if (userEmail) {
    for (const team of Object.values(teams)) {
      if (team.id === DEFAULT_GROUP_ID) continue;
      if (team.members[userEmail]) { userTeam = team; break; }
    }
  }
  return { userTeam, defaultTeam: teams[DEFAULT_GROUP_ID] || null };
}

/**
 * Standalone (non-hook) resolver for a single chat label's permission.
 * Safe to call from Zustand stores / callbacks. Mirrors the resolution used by
 * usePermissions().getLabelPermission so the store and UI never disagree.
 * Admins always get 'full_access'.
 */
export function getLabelPermissionStandalone(labelKey: string): LabelPermissionLevel {
  const { selectedAdminUserId } = useAuthStore.getState();
  const isAdmin = !selectedAdminUserId;
  if (isAdmin) return 'full_access';

  // Same fail-closed load window as getSenderPermissionStandalone: 'view_only'
  // keeps labels readable while blocking changes until the snapshot lands. (KAN-14)
  if (!useTeamStore.getState().teamsLoaded) {
    void useTeamStore.getState().loadTeams();
    return 'view_only';
  }

  const { userTeam, defaultTeam } = resolveTeamsStandalone();
  return resolvePermission(userTeam, defaultTeam, 'labels', labelKey, 'view_and_apply');
}

/** True when a label permission allows changing/applying/removing the label. */
export function labelPermissionAllowsModify(level: LabelPermissionLevel): boolean {
  return level === 'view_and_apply' || level === 'full_access';
}

/** Read a group's explicit "Manage Labels" toggle, or undefined when unset. */
function readManageLabelsFlag(team: Team | null): boolean | undefined {
  const v = team?.permissions?.labels?.global?.users_can_manage_labels;
  return typeof v === 'boolean' ? v : undefined;
}

/** One group's OWN "Manage Labels" opinion: its explicit flag, else its legacy
 *  category default. ANY valid own default is an opinion (mirrors resolvePermission,
 *  which stops at the first valid own default) — 'full_access' grants management,
 *  every other valid level (view_only / view_and_apply / no_access) denies it.
 *  `undefined` — no flag AND no valid own default — means the group has no
 *  opinion → inherit. An invalid/legacy-sentinel default is treated as absent. */
function ownManageLabelsSignal(team: Team | null): boolean | undefined {
  const flag = readManageLabelsFlag(team);
  if (typeof flag === 'boolean') return flag;
  const def = team?.permissions?.labels?.default;
  if (def && (PERMISSION_LEVELS.labels as readonly string[]).includes(def)) {
    return def === 'full_access';
  }
  return undefined;
}

/**
 * Whether a group's members may create custom chat labels.
 *
 * Precedence mirrors resolvePermission: the user's OWN group is consulted in
 * full (explicit flag, THEN its legacy full_access default) before the _default
 * group (its flag, then its legacy default). The two groups must be consulted
 * in that per-group order — not "all flags first, then all defaults" — otherwise
 * the Default group's flag shadows the own group's legacy default, which both
 * disagrees with migrateLabelsCategory (silent toggle flip) and breaks the
 * own-before-default precedence every other resolver uses (MR!199 review #3).
 *
 * Exported so the Teams editor can display each group's EFFECTIVE manage state
 * (including inheritance from the Default group) rather than only its own flag.
 */
export function resolveCanManageLabels(userTeam: Team | null, defaultTeam: Team | null): boolean {
  return ownManageLabelsSignal(userTeam) ?? ownManageLabelsSignal(defaultTeam) ?? false;
}

/** Read a group's explicit "Delete Labels" toggle, or undefined when unset. */
function readDeleteLabelsFlag(team: Team | null): boolean | undefined {
  const v = team?.permissions?.labels?.global?.users_can_delete_labels;
  return typeof v === 'boolean' ? v : undefined;
}

/**
 * Whether a group's members may delete custom chat labels org-wide.
 *
 * Deliberately separate from Manage Labels and with NO legacy fallback: every
 * existing group starts OFF, so a group that could only add labels never gains
 * delete until an admin switches it on. Own group's flag first, then the
 * Default group's, else off. Exported for the Teams editor's effective display.
 */
export function resolveCanDeleteLabels(userTeam: Team | null, defaultTeam: Team | null): boolean {
  return readDeleteLabelsFlag(userTeam) ?? readDeleteLabelsFlag(defaultTeam) ?? false;
}

/**
 * Standalone (non-hook) resolver for the "Manage Labels" permission — safe to
 * call from Zustand stores / callbacks. Mirrors usePermissions().canManageLabels
 * so the store guard and the picker UI never disagree. Admins always may manage.
 */
export function getCanManageLabelsStandalone(): boolean {
  const { selectedAdminUserId } = useAuthStore.getState();
  if (!selectedAdminUserId) return true; // admin — unrestricted

  // Fail closed during the load window, matching the label modify gating above:
  // block management until the team snapshot lands (KAN-14).
  if (!useTeamStore.getState().teamsLoaded) {
    void useTeamStore.getState().loadTeams();
    return false;
  }

  const { userTeam, defaultTeam } = resolveTeamsStandalone();
  return resolveCanManageLabels(userTeam, defaultTeam);
}

/**
 * Standalone (non-hook) resolver for the "Delete Labels" permission — mirrors
 * usePermissions().canDeleteLabels so the store guard and the picker agree.
 * Admins always may delete; shared users fail closed until teams load.
 */
export function getCanDeleteLabelsStandalone(): boolean {
  const { selectedAdminUserId } = useAuthStore.getState();
  if (!selectedAdminUserId) return true; // admin — unrestricted

  if (!useTeamStore.getState().teamsLoaded) {
    void useTeamStore.getState().loadTeams();
    return false;
  }

  const { userTeam, defaultTeam } = resolveTeamsStandalone();
  return resolveCanDeleteLabels(userTeam, defaultTeam);
}

/**
 * Standalone (non-hook) sender permission lookup, mirroring the hook's
 * resolution exactly. Safe to call from Zustand stores, event handlers and other
 * non-React contexts so enforcement does not depend on a component remembering
 * to check.
 */
export function getSenderPermissionStandalone(senderId: string): SenderPermissionLevel {
  const { selectedAdminUserId } = useAuthStore.getState();
  if (!selectedAdminUserId) return 'full_access'; // admin — unrestricted

  // Load window: before the team snapshot arrives, resolution would hit the
  // permissive fallback and let a restricted user act. view_only fails closed
  // for outbound while still letting the sender be seen (KAN-14). Kick a load
  // as well, so a boot path that never subscribed self-heals instead of
  // staying read-only forever.
  if (!useTeamStore.getState().teamsLoaded) {
    void useTeamStore.getState().loadTeams();
    return 'view_only';
  }

  const { userTeam, defaultTeam } = resolveTeamsStandalone();
  return resolvePermission<SenderPermissionLevel>(
    userTeam, defaultTeam, 'senders', senderId, 'view_and_send'
  );
}

/** True when the level permits outbound activity (sending, reacting, retrying). */
export function senderPermissionAllowsSend(level: SenderPermissionLevel | string | undefined): boolean {
  return level === 'view_and_send' || level === 'full_access';
}

/** True when the sender must be hidden from the user entirely. */
export function senderPermissionHidesSender(level: SenderPermissionLevel | string | undefined): boolean {
  return level === 'no_access';
}

/**
 * Hook that resolves the current user's effective permissions from team groups.
 *
 * Admins bypass all restrictions. Shared/team users get permissions resolved
 * from their assigned group (falling back to the _default group).
 */
export function usePermissions() {
  const teams = useTeamStore((s) => s.teams);
  const teamsLoaded = useTeamStore((s) => s.teamsLoaded);
  const selectedAdminUserId = useAuthStore((s) => s.selectedAdminUserId);
  // Normalise casing — team member emails are stored lower-cased (see the note
  // in getInboxPermissions above).
  const userEmail = useAuthStore((s) => s.user?.email?.toLowerCase());

  // Admin = no selectedAdminUserId (i.e. user is the admin, not a shared user)
  const isAdmin = !selectedAdminUserId;

  // onSnapshot fires from local cache synchronously, so teamsLoaded is true
  // before components render. permissionsReady kept for edge cases (first-ever load).
  const permissionsReady = isAdmin || teamsLoaded;

  // Find the user's specific team (not _default) — first match wins
  const { userTeam, defaultTeam } = useMemo(() => {
    const def = teams[DEFAULT_GROUP_ID] || null;
    if (!userEmail || isAdmin) return { userTeam: null, defaultTeam: def };

    let found: Team | null = null;
    for (const team of Object.values(teams)) {
      if (team.id === DEFAULT_GROUP_ID) continue;
      if (team.members[userEmail]) {
        found = team;
        break;
      }
    }
    return { userTeam: found, defaultTeam: def };
  }, [teams, userEmail, isAdmin]);

  const getSenderPermission = useMemo(() => {
    if (isAdmin) return (_id: string): SenderPermissionLevel => 'full_access';
    return (senderId: string): SenderPermissionLevel =>
      resolvePermission(userTeam, defaultTeam, 'senders', senderId, 'view_and_send');
  }, [isAdmin, userTeam, defaultTeam]);

  const getLabelPermission = useMemo(() => {
    if (isAdmin) return (_key: string): LabelPermissionLevel => 'full_access';
    return (labelKey: string): LabelPermissionLevel =>
      resolvePermission(userTeam, defaultTeam, 'labels', labelKey, 'view_and_apply');
  }, [isAdmin, userTeam, defaultTeam]);

  const getAssigneePermission = useMemo(() => {
    if (isAdmin) return (_email: string): AssigneePermissionLevel => 'full_access';
    return (email: string): AssigneePermissionLevel =>
      resolvePermission(userTeam, defaultTeam, 'assignees', email, 'full_access');
  }, [isAdmin, userTeam, defaultTeam]);

  const inboxPermissions = useMemo(() => {
    if (isAdmin) return { canViewOtherUsersChats: true, canViewUnassignedChats: true };

    // Specific group overrides default group
    const source = userTeam?.permissions?.inbox?.global
      ?? defaultTeam?.permissions?.inbox?.global;

    return {
      canViewOtherUsersChats: source?.users_can_view_other_users_chats ?? true,
      canViewUnassignedChats: source?.users_can_view_unassigned_chats ?? true,
    };
  }, [isAdmin, userTeam, defaultTeam]);

  const canManageLabels = useMemo(() => {
    if (isAdmin) return true;
    return resolveCanManageLabels(userTeam, defaultTeam);
  }, [isAdmin, userTeam, defaultTeam]);

  const canDeleteLabels = useMemo(() => {
    if (isAdmin) return true;
    return resolveCanDeleteLabels(userTeam, defaultTeam);
  }, [isAdmin, userTeam, defaultTeam]);

  const callPermissions = useMemo(() => {
    if (isAdmin) {
      return {
        restrictToOwnCalls: false,
        canUseWhatsAppCalling: true,
        canMakeOutboundCalls: true,
        canViewRecordings: true,
        hiddenCallerIds: [] as string[],
      };
    }

    // Specific group overrides default group
    const source = userTeam?.permissions?.calls?.global
      ?? defaultTeam?.permissions?.calls?.global;
    const g = source as Record<string, unknown> | undefined;

    return {
      restrictToOwnCalls: (g?.users_can_only_view_allowed_senders_calls_and_logs as boolean) ?? false,
      canUseWhatsAppCalling: (g?.users_can_use_whatsapp_calling as boolean) ?? true,
      canMakeOutboundCalls: (g?.users_can_make_outbound_calls as boolean) ?? true,
      canViewRecordings: (g?.users_can_view_recordings as boolean) ?? true,
      hiddenCallerIds: (g?.hidden_caller_ids as string[]) ?? [],
    };
  }, [isAdmin, userTeam, defaultTeam]);

  return {
    isAdmin,
    /** False while team permissions are still loading for shared users */
    permissionsReady,
    getSenderPermission,
    getLabelPermission,
    getAssigneePermission,
    /** Group-wide "Manage Labels" toggle — may the user add custom chat labels. */
    canManageLabels,
    /** Group-wide "Delete Labels" toggle — may the user delete custom chat labels (off by default). */
    canDeleteLabels,
    inboxPermissions,
    callPermissions,
  };
}
